import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  FileText, 
  Download, 
  Printer,
  CheckCircle2,
  AlertCircle,
  Shield,
  ClipboardCheck,
  FileCheck,
  Calendar,
  Building2,
  Lock,
  Users,
  Database,
  Search,
  Boxes,
  Truck,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Compliance data structure
const COMPLIANCE_SECTIONS = [
  {
    id: 'traceability',
    title: '1. Traceability Requirements',
    standard: 'FSSC 22000 Clause 8.3 / BRC Clause 3.9',
    icon: Search,
    items: [
      { requirement: 'Unique batch identification', status: 'compliant', detail: 'Each batch has unique batch_code (enforced via duplicate check)' },
      { requirement: 'Production date tracking', status: 'compliant', detail: 'production_date field on all batches' },
      { requirement: 'Use-by/expiry date tracking', status: 'compliant', detail: 'use_by_date field on all batches' },
      { requirement: 'Product identification', status: 'compliant', detail: 'Linked via product_id with full product details' },
      { requirement: 'Quantity tracking', status: 'compliant', detail: 'quantity_produced, quantity_available, quantity_dispatched fields' },
      { requirement: 'Forward traceability (one-step)', status: 'compliant', detail: 'derived_batches in /batches/{id}/traceability API' },
      { requirement: 'Backward traceability (one-step)', status: 'compliant', detail: 'source_chain in /batches/{id}/traceability API' },
      { requirement: 'Customer/dispatch linking', status: 'compliant', detail: 'Dispatch lines link batches to customers' },
    ]
  },
  {
    id: 'recall',
    title: '2. Recall Capability',
    standard: 'FSSC 22000 Clause 8.9.5 / BRC Clause 3.11',
    icon: Truck,
    items: [
      { requirement: 'Product recall procedure', status: 'compliant', detail: '/recall/search endpoint available' },
      { requirement: 'Recall export capability', status: 'compliant', detail: '/recall/export CSV export' },
      { requirement: 'Search by batch code', status: 'compliant', detail: 'Batch code search in recall' },
      { requirement: 'Search by product', status: 'compliant', detail: 'Product SKU search in recall' },
      { requirement: 'Search by customer', status: 'compliant', detail: 'Customer name search in recall' },
      { requirement: 'Search by date range', status: 'compliant', detail: 'Date range filtering available' },
    ]
  },
  {
    id: 'qc',
    title: '3. QC/Quality Control',
    standard: 'BRC Clause 5.4 / 21 CFR Part 11',
    icon: Shield,
    items: [
      { requirement: 'Hold status tracking', status: 'compliant', detail: 'qc_status: "on_hold" state' },
      { requirement: 'Release authorization', status: 'compliant', detail: 'qc_released_by, qc_released_at recorded' },
      { requirement: 'Rejection handling', status: 'compliant', detail: 'qc_rejected_by, qc_rejected_at recorded' },
      { requirement: 'Only released batches can ship', status: 'compliant', detail: 'Dispatch validation checks qc_status' },
      { requirement: 'Electronic signatures', status: 'compliant', detail: 'Password confirmation required for QC release/reject' },
      { requirement: 'User identification', status: 'compliant', detail: 'user_id, user_email logged on all actions' },
      { requirement: 'Timestamp accuracy', status: 'compliant', detail: 'ISO 8601 UTC timestamps' },
    ]
  },
  {
    id: 'capa',
    title: '4. Corrective & Preventive Action (CAPA)',
    standard: 'FSSC 22000 Clause 10.1 / HACCP Principle 5',
    icon: ClipboardCheck,
    items: [
      { requirement: 'Corrective action documentation', status: 'compliant', detail: 'corrective_action required field on rejection' },
      { requirement: 'Due date tracking', status: 'compliant', detail: 'corrective_action_due_date field' },
      { requirement: 'Completion verification', status: 'compliant', detail: 'corrective_action_completed tracking' },
      { requirement: 'Completion sign-off', status: 'compliant', detail: 'corrective_action_completed_by with password' },
      { requirement: 'Root cause analysis field', status: 'enhancement', detail: 'Consider adding dedicated root_cause field' },
    ]
  },
  {
    id: 'audit',
    title: '5. Audit Trail',
    standard: 'BRC Clause 3.4 / 21 CFR Part 11',
    icon: Database,
    items: [
      { requirement: 'Who (user identification)', status: 'compliant', detail: 'user_id, user_email on all logs' },
      { requirement: 'What (action performed)', status: 'compliant', detail: 'action field (create, update, delete, qc_update)' },
      { requirement: 'When (timestamp)', status: 'compliant', detail: 'timestamp in ISO 8601 format' },
      { requirement: 'Before value', status: 'compliant', detail: 'before_value JSON field' },
      { requirement: 'After value', status: 'compliant', detail: 'after_value JSON field' },
      { requirement: 'Entity identification', status: 'compliant', detail: 'entity_type, entity_id fields' },
      { requirement: 'Audit log retention', status: 'compliant', detail: 'audit_log_retention_days setting (default 30)' },
      { requirement: 'Log immutability', status: 'enhancement', detail: 'Consider write-once storage or blockchain anchoring' },
    ]
  },
  {
    id: 'haccp',
    title: '6. HACCP Specific Requirements',
    standard: 'Codex Alimentarius / HACCP Principles',
    icon: FileCheck,
    items: [
      { requirement: 'CCP identification', status: 'partial', detail: 'CCPs defined in HACCP plan, not automated in system' },
      { requirement: 'Monitoring records', status: 'compliant', detail: 'Batch records serve as production monitoring' },
      { requirement: 'Corrective actions', status: 'compliant', detail: 'CAPA fields on rejected batches' },
      { requirement: 'Verification activities', status: 'compliant', detail: 'Audit logs track all QC decisions' },
      { requirement: 'Documentation', status: 'partial', detail: 'Hazard analysis maintained externally' },
    ]
  }
];

export default function ComplianceChecklist() {
  const navigate = useNavigate();
  const printRef = useRef();
  const [printing, setPrinting] = useState(false);

  // Calculate compliance stats
  const stats = COMPLIANCE_SECTIONS.reduce((acc, section) => {
    section.items.forEach(item => {
      acc.total++;
      if (item.status === 'compliant') acc.compliant++;
      else if (item.status === 'partial') acc.partial++;
      else if (item.status === 'enhancement') acc.enhancement++;
    });
    return acc;
  }, { total: 0, compliant: 0, partial: 0, enhancement: 0 });

  const complianceScore = Math.round((stats.compliant / stats.total) * 100);

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  const handleExportPDF = () => {
    toast.info('Opening print dialog for PDF export. Select "Save as PDF" as your printer.');
    handlePrint();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
      case 'partial':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      case 'enhancement':
        return <AlertCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'compliant':
        return <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Compliant</span>;
      case 'partial':
        return <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Partial</span>;
      case 'enhancement':
        return <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Enhancement</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">Unknown</span>;
    }
  };

  const currentDate = new Date().toLocaleDateString('en-IE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="space-y-6" data-testid="compliance-checklist-page">
      {/* Header - Non-print */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reports
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Food Safety Compliance Checklist</h1>
            <p className="text-muted-foreground">FSSC 22000 / BRC / HACCP Requirements</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} disabled={printing}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleExportPDF} disabled={printing}>
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="print:p-0">
        {/* Print Header */}
        <div className="hidden print:block print:mb-8">
          <div className="flex items-center justify-between border-b-2 border-emerald-600 pb-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-emerald-800">Food Safety Compliance Checklist</h1>
              <p className="text-sm text-gray-600">FSSC 22000 | BRC Global Standards | HACCP</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">Cashel Blue Operations Hub</p>
              <p className="text-xs text-gray-600">Generated: {currentDate}</p>
            </div>
          </div>
        </div>

        {/* Compliance Score Summary */}
        <Card className="mb-6 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white print:border print:shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center print:w-16 print:h-16">
                  <span className="text-3xl font-bold text-emerald-700 print:text-2xl">{complianceScore}%</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-emerald-800">Overall Compliance Score</h2>
                  <p className="text-sm text-gray-600">Based on {stats.total} requirements across 6 standards</p>
                </div>
              </div>
              <div className="flex gap-6 print:gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600 print:text-xl">{stats.compliant}</div>
                  <div className="text-xs text-gray-600">Compliant</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600 print:text-xl">{stats.partial}</div>
                  <div className="text-xs text-gray-600">Partial</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 print:text-xl">{stats.enhancement}</div>
                  <div className="text-xs text-gray-600">Enhancement</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Standards Legend */}
        <div className="grid grid-cols-3 gap-4 mb-6 print:gap-2 print:mb-4">
          <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg print:p-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 print:w-4 print:h-4" />
            <div>
              <p className="font-medium text-sm">Compliant</p>
              <p className="text-xs text-gray-600">Fully meets requirement</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg print:p-2">
            <AlertCircle className="w-5 h-5 text-amber-600 print:w-4 print:h-4" />
            <div>
              <p className="font-medium text-sm">Partial</p>
              <p className="text-xs text-gray-600">Partially implemented</p>
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg print:p-2">
            <AlertCircle className="w-5 h-5 text-blue-600 print:w-4 print:h-4" />
            <div>
              <p className="font-medium text-sm">Enhancement</p>
              <p className="text-xs text-gray-600">Recommended improvement</p>
            </div>
          </div>
        </div>

        {/* Compliance Sections */}
        <div className="space-y-6 print:space-y-4">
          {COMPLIANCE_SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            const sectionCompliant = section.items.filter(i => i.status === 'compliant').length;
            const sectionTotal = section.items.length;
            const sectionScore = Math.round((sectionCompliant / sectionTotal) * 100);

            return (
              <Card key={section.id} className="overflow-hidden print:break-inside-avoid print:shadow-none print:border">
                <CardHeader className="bg-gray-50 py-4 print:py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center print:w-8 print:h-8">
                        <SectionIcon className="w-5 h-5 text-emerald-600 print:w-4 print:h-4" />
                      </div>
                      <div>
                        <CardTitle className="text-lg print:text-base">{section.title}</CardTitle>
                        <CardDescription className="text-xs">{section.standard}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <span className="text-lg font-bold text-emerald-700 print:text-base">{sectionScore}%</span>
                        <p className="text-xs text-gray-600">{sectionCompliant}/{sectionTotal} items</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50/50">
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-600 w-8">Status</th>
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-600">Requirement</th>
                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-600 print:hidden">Implementation Details</th>
                        <th className="text-center px-4 py-2 text-xs font-medium text-gray-600 w-24 print:w-20">Result</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {section.items.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50 print:hover:bg-transparent">
                          <td className="px-4 py-3 print:py-2">
                            {getStatusIcon(item.status)}
                          </td>
                          <td className="px-4 py-3 print:py-2">
                            <span className="font-medium text-sm">{item.requirement}</span>
                            <p className="text-xs text-gray-500 mt-0.5 hidden print:block">{item.detail}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 print:hidden">
                            {item.detail}
                          </td>
                          <td className="px-4 py-3 text-center print:py-2">
                            {getStatusBadge(item.status)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* API Reference Section */}
        <Card className="mt-6 print:mt-4 print:break-inside-avoid print:shadow-none print:border">
          <CardHeader className="bg-blue-50 py-4 print:py-3">
            <CardTitle className="text-lg flex items-center gap-2 print:text-base">
              <FileText className="w-5 h-5 text-blue-600 print:w-4 print:h-4" />
              API Endpoints for Auditors
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 print:p-3">
            <div className="grid grid-cols-2 gap-4 print:gap-2 print:text-sm">
              <div>
                <h4 className="font-medium text-sm mb-2">Traceability</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/batches/{'{id}'}/traceability</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/reports/traceability</code></li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">Recall</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li><code className="bg-gray-100 px-1 rounded">POST /api/recall/search</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/recall/export</code></li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">QC Management</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li><code className="bg-gray-100 px-1 rounded">POST /api/batches/{'{id}'}/qc-status</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">POST /api/batches/{'{id}'}/complete-corrective-action</code></li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-sm mb-2">Audit Logs</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/audit-logs</code></li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Certification Checklist */}
        <Card className="mt-6 print:mt-4 print:break-inside-avoid print:shadow-none print:border">
          <CardHeader className="bg-amber-50 py-4 print:py-3">
            <CardTitle className="text-lg flex items-center gap-2 print:text-base">
              <ClipboardCheck className="w-5 h-5 text-amber-600 print:w-4 print:h-4" />
              Pre-Audit Certification Checklist
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 print:p-3">
            <div className="grid grid-cols-2 gap-3 print:gap-2">
              {[
                'Verify all batches have unique codes (no duplicates)',
                'Ensure all QC releases have electronic signatures',
                'Check rejected batches have corrective actions',
                'Run a mock recall test using /recall/search',
                'Review audit log retention settings',
                'Verify traceability for sample batches',
                'Ensure dispatched products trace to released batches',
                'Review user access permissions'
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-white border rounded print:p-1">
                  <div className="w-4 h-4 border-2 border-gray-300 rounded mt-0.5 print:w-3 print:h-3" />
                  <span className="text-sm print:text-xs">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 pt-4 border-t text-center text-xs text-gray-500 print:mt-4 print:pt-2">
          <p>Document generated by Cashel Blue Operations Hub</p>
          <p>This checklist is for internal compliance verification purposes.</p>
          <p className="mt-1">Generated: {currentDate} | Version 1.0</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
          nav, header, footer, .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
